/**
 * Environment Configuration
 * 
 * Centralized environment variable management with validation
 * and type safety for the Courtly application.
 */

import { z } from 'zod';

// Environment variable schema with validation
const envSchema = z.object({
  // Application
  NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),
  NEXT_PUBLIC_APP_NAME: z.string().default('Courtly'),
  NEXT_PUBLIC_APP_VERSION: z.string().default('1.0.0'),
  
  // Sample Data Mode
  NEXT_PUBLIC_USE_SAMPLE_DATA: z.coerce.boolean().default(false),

  // Firebase Configuration
  NEXT_PUBLIC_FIREBASE_API_KEY: z.string().optional(),
  NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN: z.string().optional(),
  NEXT_PUBLIC_FIREBASE_PROJECT_ID: z.string().optional(),
  NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET: z.string().optional(),
  NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID: z.string().optional(),
  NEXT_PUBLIC_FIREBASE_APP_ID: z.string().optional(),
  NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID: z.string().optional(),
  NEXT_PUBLIC_FIREBASE_TENANT_ID: z.string().optional(),

  // Security Settings
  RATE_LIMIT_LOGIN_MAX_ATTEMPTS: z.coerce.number().default(5),
  RATE_LIMIT_LOGIN_WINDOW_MS: z.coerce.number().default(180000),
  RATE_LIMIT_LOGIN_BLOCK_DURATION_MS: z.coerce.number().default(360000),

  RATE_LIMIT_REGISTER_MAX_ATTEMPTS: z.coerce.number().default(3),
  RATE_LIMIT_REGISTER_WINDOW_MS: z.coerce.number().default(3600000),
  RATE_LIMIT_REGISTER_BLOCK_DURATION_MS: z.coerce.number().default(3600000),

  SESSION_TIMEOUT_WARNING_MS: z.coerce.number().default(300000),
  SESSION_ABSOLUTE_TIMEOUT_MS: z.coerce.number().default(28800000),

  // Analytics
  NEXT_PUBLIC_GOOGLE_ANALYTICS_ID: z.string().optional(),

  // Debug Flags
  DEBUG_AUTH: z.coerce.boolean().default(false),
});

// Parse environment variables
const env = envSchema.parse({
  NODE_ENV: process.env.NODE_ENV,
  NEXT_PUBLIC_APP_NAME: process.env.NEXT_PUBLIC_APP_NAME,
  NEXT_PUBLIC_APP_VERSION: process.env.NEXT_PUBLIC_APP_VERSION,
  NEXT_PUBLIC_USE_SAMPLE_DATA: process.env.NEXT_PUBLIC_USE_SAMPLE_DATA,

  // Firebase Configuration
  NEXT_PUBLIC_FIREBASE_API_KEY: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  NEXT_PUBLIC_FIREBASE_PROJECT_ID: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  NEXT_PUBLIC_FIREBASE_APP_ID: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
  NEXT_PUBLIC_FIREBASE_TENANT_ID: process.env.NEXT_PUBLIC_FIREBASE_TENANT_ID,

  // Security Settings
  RATE_LIMIT_LOGIN_MAX_ATTEMPTS: process.env.RATE_LIMIT_LOGIN_MAX_ATTEMPTS,
  RATE_LIMIT_LOGIN_WINDOW_MS: process.env.RATE_LIMIT_LOGIN_WINDOW_MS,
  RATE_LIMIT_LOGIN_BLOCK_DURATION_MS: process.env.RATE_LIMIT_LOGIN_BLOCK_DURATION_MS,

  RATE_LIMIT_REGISTER_MAX_ATTEMPTS: process.env.RATE_LIMIT_REGISTER_MAX_ATTEMPTS,
  RATE_LIMIT_REGISTER_WINDOW_MS: process.env.RATE_LIMIT_REGISTER_WINDOW_MS,
  RATE_LIMIT_REGISTER_BLOCK_DURATION_MS: process.env.RATE_LIMIT_REGISTER_BLOCK_DURATION_MS,

  SESSION_TIMEOUT_WARNING_MS: process.env.SESSION_TIMEOUT_WARNING_MS,
  SESSION_ABSOLUTE_TIMEOUT_MS: process.env.SESSION_ABSOLUTE_TIMEOUT_MS,

  // Analytics
  NEXT_PUBLIC_GOOGLE_ANALYTICS_ID: process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID,

  // Debug Flags
  DEBUG_AUTH: process.env.DEBUG_AUTH,
});

// Environment helpers
export const isDevelopment = env.NODE_ENV === 'development';
export const isProduction = env.NODE_ENV === 'production';
export const isTest = env.NODE_ENV === 'test';

// Sample data mode helpers
export const USE_SAMPLE_DATA = env.NEXT_PUBLIC_USE_SAMPLE_DATA;

/**
 * Check if we should use sample data for non-authentication entities
 * Note: Authentication always uses Firebase Auth regardless of this setting
 */
export function isSampleDataMode(): boolean {
  return USE_SAMPLE_DATA;
}

// Validation for production environment
function validateProductionConfig(): void {
  if (!isProduction) return;

  const errors: string[] = [];

  // Check Firebase configuration
  if (!env.NEXT_PUBLIC_FIREBASE_API_KEY) {
    errors.push('NEXT_PUBLIC_FIREBASE_API_KEY is required in production');
  }
  if (!env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN) {
    errors.push('NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN is required in production');
  }
  if (!env.NEXT_PUBLIC_FIREBASE_PROJECT_ID) {
    errors.push('NEXT_PUBLIC_FIREBASE_PROJECT_ID is required in production');
  }
  if (!env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET) {
    errors.push('NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET is required in production');
  }
  if (!env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID) {
    errors.push('NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID is required in production');
  }
  if (!env.NEXT_PUBLIC_FIREBASE_APP_ID) {
    errors.push('NEXT_PUBLIC_FIREBASE_APP_ID is required in production');
  }

  if (errors.length > 0) {
    console.error('❌ Production Configuration Errors:');
    errors.forEach(error => console.error(`    ${error}`));
    throw new Error('Invalid production environment configuration');
  }
}

// Track if environment config has been logged
let hasLoggedEnvConfig = false;

// Log environment configuration
function logEnvironmentConfig(): void {
  if (isTest || hasLoggedEnvConfig) return;

  console.log('\n🔧 Environment Configuration:');
  console.log('-----------------------------');
  console.log(`Environment: ${env.NODE_ENV}`);
  console.log(`Application: ${env.NEXT_PUBLIC_APP_NAME} v${env.NEXT_PUBLIC_APP_VERSION}`);
  console.log(`Sample Data Mode: ${USE_SAMPLE_DATA ? 'enabled (demo data only)' : 'disabled'}`);
  
  if (isDevelopment) {
    console.log(`Firebase Project: ${env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || 'not configured'}`);
    console.log(`Firebase Auth: always enabled`);
    console.log(`Debug Auth: ${env.DEBUG_AUTH ? 'enabled' : 'disabled'}`);
  }
  
  hasLoggedEnvConfig = true;
}

// Initialize environment
validateProductionConfig();
logEnvironmentConfig();

// Export the main environment object
export { env };

// Type for environment configuration
export type EnvConfig = typeof env;
